import React, { useState, useCallback, useEffect, useMemo } from "react";

// recoil
import { useRecoilValue, useSetRecoilState } from "recoil";
import { userCredentials } from "recoilState/GlobalState";

// utils
import { useFormik } from "formik";
import { vouchersDetails as titles } from "consts/titulos-de-tablas";

// styles
import s from "styles/pages/Pages.module.scss";
import XGrid from "components/UI/Grid/XGrid";
import { useService } from "components/route/ServiceRoute";
import { snackbarData } from "recoilState/GlobalState";
import axiosPost from "services/services-mia/axiosPost";
import { connectionRolGet /* , rolGet */ } from "services/services-mia";
import { FilterAltOffRounded, DeleteOutlined, Cached } from "@mui/icons-material";
import { Buttons, HeaderButton } from "components";
import Filters from "components/Filters";
import { IconDni, UserIconito } from "assets/icons";
import Modal from "components/UI/Modal/Modal";
import DeleteModal from "./DeleteModal";

const HABITACION_HUESPED_STATUS_LABELS = {
    I: "Check-in",
    R: "Reservado",
    O: "Check-out",
    C: "Cancelado",
    D: "Disponible",
};

const ESTADOS_VOUCHER = [
    { id: "", label: "Todos" },
    { id: "I", label: "Check-in" },
    { id: "R", label: "Reservado" },
    { id: "O", label: "Check-out" },
    { id: "D", label: "Disponible" },
];

const formatHabitacionHuespedStatus = (status) =>
    HABITACION_HUESPED_STATUS_LABELS[status] ?? status ?? "";

const formatFechaEstado = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    // Formateamos solo la fecha: "17/05/2026"
    const fecha = date.toLocaleDateString("es-ES", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    });

    // Formateamos solo la hora: "17:17"
    const hora = date.toLocaleTimeString("es-ES", {
        hour: "2-digit",
        minute: "2-digit",
    });

    // Los unimos con un espacio limpio en el medio
    return `${fecha} ${hora}`;
};

const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const baseDate = dateStr.slice(0, 10);
    const parts = baseDate.split("-");
    if (parts.length === 3) {
        const [year, month, day] = parts;
        return `${day}/${month}/${year}`;
    }
    return baseDate.replace(/-/g, "/");
};

const formatDNI = (dni) => {
    return dni.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

export default function VoucherDetailModal({ loteVoucherId, updateLotes }) {
    const [modalDetails, setModalDetails] = useState({ isOpen: false });
    const [isLoading, setIsLoading] = useState(false);
    const [rowSelected, setRowSelected] = useState({ status: false, row: {} });
    const [allRows, setAllRows] = useState([]);
    const [connectionRoles, setConnectionRoles] = useState([]);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const credentials = useRecoilValue(userCredentials);
    const setSnackbarInfo = useSetRecoilState(snackbarData);
    const [huesped, setHuesped] = useState({ nombres: "", apellidos: "", dni: "" });

    const { service } = useService();

    const VOUCHER_ESTADOS = {
        DISPONIBLE: "Disponible",
        CONSUMIDO: "Consumido",
        VENCIDO: "Vencido",
    };

    const getVoucherEstado = (status, fechaHasta) => {
        if (status !== "D") return VOUCHER_ESTADOS.CONSUMIDO;
        const hoy = new Date().toISOString().slice(0, 10);
        const hasta = fechaHasta?.slice(0, 10);
        if (hasta && hoy > hasta) return VOUCHER_ESTADOS.VENCIDO;
        return VOUCHER_ESTADOS.DISPONIBLE;
    };

    const updateList = useCallback(async () => {
        setRowSelected({ status: false, row: {} });
        setIsLoading(true);

        const body = {
            service: "SFVoucherGet",
            params: {
                SFLoteVoucherId: loteVoucherId,
            },
        };

        try {
            const { result } = await axiosPost(body, credentials);

            const firstIndexByReserva = new Map();
            result.forEach((item, index) => {
                const id = item?.SFHabitacionHuespedId;
                if (!firstIndexByReserva.has(id)) {
                    firstIndexByReserva.set(id, index);
                }
            });

            setAllRows(
                result.map((item, index) => {
                    const {
                        SFVoucherId,
                        SFVoucherLoteId,
                        SFVoucherTipo,
                        SFVoucherNumero,
                        SFVoucherFecha,
                        SFVoucherFechaDesde,
                        SFVoucherFechaHasta,
                        SFVoucherVersion,
                        SFHabitacionHuespedStatus,
                        SFHabitacionHuespedEstadoUfh,
                        merchantServicioUsuarioDniX,
                        SFHabitacionHuespedReserva,
                        merchantServicioUsuarioApellidos,
                        merchantServicioUsuarioNombres,
                        SFHabitacionNumero,
                        SFHabitacionPiso,
                        SFHotelNombre,
                    } = item;

                    setHuesped({ nombres: merchantServicioUsuarioNombres, apellidos: merchantServicioUsuarioApellidos, dni: formatDNI(merchantServicioUsuarioDniX) });

                    const firstReserva =
                        firstIndexByReserva.get(item.SFHabitacionHuespedId) ===
                        index || !item.SFHabitacionHuespedId;
                    return {
                        id: SFVoucherId,
                        SFVoucherId,
                        SFVoucherLoteId,
                        SFVoucherTipo,
                        SFVoucherNumero,
                        SFVoucherFecha,
                        SFVoucherFechaDesde,
                        SFVoucherFechaHasta,
                        SFVoucherVersion,
                        SFHabitacionHuespedStatus,
                        SFHabitacionHuespedEstadoUfh,
                        SFHabitacionHuespedReserva,
                        merchantServicioUsuarioDniX,
                        merchantServicioUsuarioApellidos,
                        merchantServicioUsuarioNombres,
                        SFHabitacionNumero,
                        SFHabitacionPiso,
                        SFHotelNombre,

                        [titles[0].field]: SFVoucherNumero,
                        [titles[1].field]: getVoucherEstado(SFHabitacionHuespedStatus, SFVoucherFechaHasta),
                        [titles[2].field]: formatDate(SFHabitacionHuespedReserva),
                        [titles[3].field]: SFHotelNombre,

                        [titles[4].field]: SFVoucherFecha
                            ? formatDate(SFVoucherFecha)
                            : "",
                        [titles[5].field]: SFHabitacionHuespedStatus === 'D' ? '' : formatHabitacionHuespedStatus(
                            SFHabitacionHuespedStatus
                        ),
                        [titles[6].field]: formatFechaEstado(SFHabitacionHuespedEstadoUfh)
                    };
                })
            );

        } catch (error) {
            console.error("Error al obtener los hoteles:", error);
            setSnackbarInfo({
                open: true,
                severity: "error",
                message: error?.message || "Error al obtener los hoteles",
            });
        } finally {
            setIsLoading(false);
        }
    }, [credentials, setSnackbarInfo, loteVoucherId]);

    const formik = useFormik({
        initialValues: {
            estado: "",
        },
        onSubmit: () => { },
    });

    const rows = useMemo(() => {
        const estado = formik.values.estado;
        if (!estado) return allRows;
        return allRows.filter((row) => row.SFHabitacionHuespedStatus === estado);
    }, [allRows, formik.values.estado]);

    const closeModal = () => {
        setModalDetails({ isOpen: false });
    };

    useEffect(() => {
        updateList();
    }, [updateList]);

    const resetFilters = () => {
        formik.setFieldValue("estado", "");
    };

    useEffect(() => {
        if (Object.keys(credentials).length > 0 && service?.servicioCod) {
            (async () => {
                const connectionRoles = await connectionRolGet(
                    service.servicioCod,
                    credentials
                );
                setConnectionRoles(connectionRoles.result);
            })();
        }
    }, [credentials, service]);

    return (
        <main className={s.main} style={{ minHeight: "65vh" }}>
            <Modal
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                title="Eliminar Voucher"
            >
                {deleteModalOpen && (
                    <DeleteModal
                        voucher={rowSelected.row}
                        closeModal={() => setDeleteModalOpen(false)}
                        updateList={() => { updateList(); updateLotes?.(); }}
                    />
                )}
            </Modal>

            <div style={{ borderRadius: "10px", backgroundColor: "#e9e9e9", display: "flex", justifyContent: "center", gap: "1rem", position: "absolute", top: "1.5rem", right: "8%", padding: '0rem 1rem' }}>
                <h4 style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <img src={UserIconito} alt="User" width="18" height="18" /> {huesped.nombres + " " + huesped.apellidos}
                </h4>

                <h4 style={{ display: "flex", fontWeight: "normal", alignItems: "center", gap: "0.5rem", paddingLeft: "1rem", borderLeft: "2px solid #aaa" }}>
                    <img src={IconDni} alt="DNI" width="18" height="18" />DNI {huesped.dni}
                </h4>
            </div>
            <div className={s["filters-actions"]}>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <Filters
                        formik={formik}
                        inputsRender={[{
                            type: "select",
                            name: "estado",
                            label: "Estado de reserva",
                            disabled: false,
                            options: ESTADOS_VOUCHER,
                            firstOptionKey: "id",
                            firstOptionName: "label",
                            firstOptionValue: "id",
                        }]}
                    />
                    <HeaderButton text={"Limpiar Filtros"} onClick={resetFilters}>
                        <FilterAltOffRounded />
                    </HeaderButton>
                </div>
                <Buttons>
                    <HeaderButton text={"Actualizar"} onClick={updateList}>
                        <Cached />
                    </HeaderButton>
                    <HeaderButton
                        text={"Eliminar Voucher"}
                        disabled={rowSelected.row.SFHabitacionHuespedStatus !== "D"}
                        onClick={() => setDeleteModalOpen(true)}
                    >
                        <DeleteOutlined />
                    </HeaderButton>
                </Buttons>
            </div>
            <div className={s.container}>
                <XGrid
                    rows={rows}
                    titles={titles}
                    loading={isLoading}
                    secondarySelected={rowSelected}
                    setSecondarySelected={setRowSelected}
                    isVoucher={true}
                />
            </div>
        </main>
    );
}
